// VOLT — the reference kit.
//   Live Wire (passive): +25% damage to targets within 260 px; player kills refund
//     40% of the base dash cooldown.
//   Arc Dash (signature): aimed invulnerable lunge that marks every enemy it passes
//     through, then chain lightning races through the marks in path order and jumps
//     onward. STORM_RUNNER: strikes twice, +30% chain range. ARC_ECHO: the path
//     discharges again 0.6 s later at 60%. SIGNATURE_EVOLVED: shock trail, +2 chains.
//   Overload (ultimate): 4.5 s of lightning waves through every enemy on screen
//     while VOLT runs 30% faster with a white-hot reactor.

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH, PLAYER_BODY } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, Vec } from '../../core/contracts';
import type { DamageInfo, StatusApplication, Tag } from '../../core/types';
import {
  arcDashExtraChains,
  edgeDistance,
  nearestNeighbourOrder,
  simplifyPath,
  sweepMarks,
  sweepPath,
  type MarkCandidate,
  type Rect,
} from './math';
import { ATEX, sfx, targetable, targetableList, texOr, type Kit, type KitHost, type TimerHandle } from './shared';

export const VOLT_TUNING = {
  liveWireRange: 260,
  liveWireBonus: 1.25,
  /** Fraction of the base dash cooldown refunded per kill. */
  killDashRefund: 0.4,
  dashDistance: 430,
  dashDuration: 0.2,
  chainDamage: 42,
  chainRange: 300,
  stormRunnerRangeMult: 1.3,
  stormRunnerDelay: 0.15,
  burstRadius: 140,
  burstDamage: 24,
  arcEchoDelay: 0.6,
  arcEchoMult: 0.6,
  evolvedTrail: { duration: 3, dps: 22 },
  dashHitstopMs: 40,
  overloadDuration: 4.5,
  overloadInterval: 0.45,
  overloadDamage: 30,
  overloadSpeed: 1.3,
} as const;

const SHOCK: StatusApplication = { id: 'shock', stacks: 1, duration: 2.5 };
const CHAIN_TAGS: Tag[] = ['electric', 'chain'];
/** Marking box: the player body plus a little forgiveness. */
const MARK_HW = PLAYER_BODY.w / 2 + 8;
const MARK_HH = PLAYER_BODY.h / 2 + 4;

interface DashState {
  x0: number;
  y0: number;
  dirX: number;
  dirY: number;
  lastX: number;
  lastY: number;
  path: Vec[];
  marked: EnemyApi[];
  markedSet: Set<EnemyApi>;
  markers: Map<EnemyApi, Phaser.GameObjects.Image>;
  ghostAt: number;
  startedAt: number;
  done: boolean;
  safety: TimerHandle;
}

interface OverloadState {
  until: number;
  pulses: TimerHandle;
  crackle: number;
  aura: Phaser.GameObjects.Image;
  ring: Phaser.GameObjects.Image;
}

export function createVoltKit(h: KitHost): Kit {
  return new VoltKit(h);
}

class VoltKit implements Kit {
  private dash: DashState | null = null;
  private overload: OverloadState | null = null;
  /** Arc echo / storm repeat timers still pending (count toward signatureActive). */
  private pending = new Set<TimerHandle>();
  private lastRefundFx = -1;

  constructor(private readonly h: KitHost) {}

  get signatureActive(): boolean {
    for (const t of this.pending) if (!t.done) return true;
    return this.dash !== null;
  }

  get ultimateActive(): boolean {
    return this.overload !== null;
  }

  start(): void {
    const { ctx } = this.h;
    // Live Wire: kills refund the dash.
    this.h.on('enemy:killed', (e) => {
      if (e.info.source !== 'player' || e.enemy.kind === 'prop') return;
      const base = ctx.player.def.movement.dashCooldown;
      this.h.safe.run('player.reduceDashCooldown', () => ctx.player.reduceDashCooldown(base * VOLT_TUNING.killDashRefund));
      if (this.h.timers.now - this.lastRefundFx > 0.12) {
        this.lastRefundFx = this.h.timers.now;
        const p = ctx.player;
        this.h.safe.run('fx.sparks', () =>
          ctx.fx.sparks(p.x - p.facing * 14, p.y + 18, Math.PI / 2 + p.facing * 0.6, { count: 5, color: COLORS.voltGlow, speed: 260 }),
        );
      }
    });
  }

  canSignature(): boolean {
    return this.dash === null;
  }

  outgoing(target: EnemyApi | null, _info: DamageInfo): number {
    if (!target) return 1;
    const p = this.h.ctx.player;
    return edgeDistance(p.x, p.y, target.x, target.y, target.radius) <= VOLT_TUNING.liveWireRange ? VOLT_TUNING.liveWireBonus : 1;
  }

  incoming(_info: DamageInfo): number {
    return 1;
  }

  // ───────────────────────────── Arc Dash ─────────────────────────────

  signature(): void {
    const { ctx, safe, timers } = this.h;
    const p = ctx.player;
    const x0 = p.x;
    const y0 = p.y;
    const aim = ctx.input.aim(x0, y0);
    const dirX = Math.cos(aim.angle);
    const dirY = Math.sin(aim.angle);
    const full = VOLT_TUNING.dashDistance * Math.max(0.3, p.stats.dashPower);

    // Stop at walls: cast from the centre and the body corners, keep the shortest.
    let travel = full;
    const hw = PLAYER_BODY.w / 2 - 3;
    const hh = PLAYER_BODY.h / 2 - 3;
    for (const [ox, oy] of [[0, 0], [hw, hh], [-hw, hh], [hw, -hh], [-hw, -hh]]) {
      const hit = safe.call(
        'world.raycast',
        () => ctx.world.raycast(x0 + ox, y0 + oy, x0 + ox + dirX * full, y0 + oy + dirY * full),
        { x: 0, y: 0, hit: false, dist: full },
      );
      if (hit.hit) travel = Math.min(travel, Math.max(0, hit.dist - 2));
    }
    const speed = full / VOLT_TUNING.dashDuration;
    const duration = Math.max(0.05, (VOLT_TUNING.dashDuration * travel) / full);

    const d: DashState = {
      x0,
      y0,
      dirX,
      dirY,
      lastX: x0,
      lastY: y0,
      path: [{ x: x0, y: y0 }],
      marked: [],
      markedSet: new Set(),
      markers: new Map(),
      ghostAt: 0,
      startedAt: timers.now,
      done: false,
      safety: timers.after(duration + 0.12, () => this.finishDash()),
    };
    this.dash = d;

    safe.run('player.overrideMotion', () =>
      p.overrideMotion({
        vx: dirX * speed,
        vy: dirY * speed,
        duration,
        gravity: false,
        invulnerable: true,
        ghost: true,
        anim: 'signature',
        onEnd: () => this.finishDash(),
      }),
    );

    // Launch: reactor burst behind the mech.
    sfx(ctx, safe, 'arc_dash');
    safe.run('fx.sparks', () =>
      ctx.fx.sparks(x0, y0, aim.angle + Math.PI, { count: 14, color: COLORS.voltGlow, speed: 620, spread: 0.9 }),
    );
    safe.run('fx.ring', () => ctx.fx.ring(x0, y0, 64, { color: COLORS.volt, width: 3, fromRadius: 16 }));
    safe.run('fx.shake', () => ctx.fx.shake(0.003, 90));
    this.stepDash();
  }

  /** Advance the dash sweep to the player's current position (final = called from finishDash). */
  private stepDash(final = false): void {
    const d = this.dash;
    if (!d || d.done) return;
    const { ctx, safe, timers } = this.h;
    const p = ctx.player;
    const x = p.x;
    const y = p.y;

    // Mark every enemy the swept body touched since the last step, in path order.
    const candidates: MarkCandidate<EnemyApi>[] = [];
    for (const e of targetableList(ctx, safe)) {
      if (d.markedSet.has(e)) continue;
      candidates.push({ item: e, areas: areasOf(e) });
    }
    for (const e of sweepMarks(d.lastX, d.lastY, x, y, MARK_HW, MARK_HH, candidates)) this.mark(d, e);

    // Afterimage streak.
    if (timers.now >= d.ghostAt) {
      d.ghostAt = timers.now + 0.028;
      safe.run('fx.afterimage', () => ctx.fx.afterimage(p.view.root, { color: COLORS.volt, alpha: 0.55 }));
    }
    if (Math.hypot(x - d.lastX, y - d.lastY) > 1) {
      safe.run('fx.trailSegment', () => ctx.fx.trailSegment(d.lastX, d.lastY, x, y, 'electric', 12));
      d.path.push({ x, y });
    }
    d.lastX = x;
    d.lastY = y;

    // Walls stop the lunge early (the raycast already shortened it; this covers moving geometry and slopes).
    if (!final && timers.now - d.startedAt > 0.03) {
      const b = safe.call('player.body', () => p.body.blocked, null);
      if (
        b &&
        ((d.dirX > 0.35 && b.right) || (d.dirX < -0.35 && b.left) || (d.dirY > 0.35 && b.down) || (d.dirY < -0.35 && b.up))
      ) {
        safe.run('player.cancelOverride', () => p.cancelOverride());
        this.finishDash();
      }
    }
  }

  private mark(d: DashState, e: EnemyApi): void {
    const { ctx, safe, bolts } = this.h;
    d.marked.push(e);
    d.markedSet.add(e);
    const n = d.marked.length;
    // Spark + small zap from the mech to the target, rising pitch per mark.
    safe.run('fx.sparks', () => ctx.fx.sparks(e.x, e.y, Math.atan2(d.dirY, d.dirX), { count: 8, color: COLORS.voltGlow, speed: 420 }));
    bolts.strike([{ x: ctx.player.x, y: ctx.player.y }, { x: e.x, y: e.y }], { color: COLORS.volt, width: 2, life: 0.14, jitter: 8, branches: false });
    sfx(ctx, safe, 'chain_zap', { volume: 0.35, pitch: 1 + 0.09 * Math.min(8, n) });

    // Marker glyph above the target until the discharge.
    const scene = ctx.scene;
    const img = this.h.track(scene.add.image(e.x, e.y - e.radius - 18, ATEX.diamond));
    img.setTint(COLORS.voltGlow).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.FX_ADD).setScale(2.2).setAlpha(0);
    scene.tweens.add({ targets: img, scale: 1, alpha: 1, duration: 120, ease: 'Back.Out' });
    d.markers.set(e, img);
  }

  private finishDash(): void {
    const d = this.dash;
    if (!d || d.done) return;
    this.stepDash(true);
    d.done = true;
    d.safety.cancel();
    this.dash = null;
    const { ctx, safe, timers, bolts } = this.h;
    const p = ctx.player;
    const end = { x: p.x, y: p.y };
    if (d.path.length === 1 || Math.hypot(end.x - d.lastX, end.y - d.lastY) > 1) d.path.push(end);

    const storm = this.h.flag('STORM_RUNNER');
    const evolved = this.h.flag('SIGNATURE_EVOLVED');
    const extras = arcDashExtraChains(safe.call('player.stats', () => p.stats.chainBonus, 0), evolved);
    const range = VOLT_TUNING.chainRange * (storm ? VOLT_TUNING.stormRunnerRangeMult : 1);

    // Electric afterglow along the whole route.
    const route = simplifyPath(d.path, 24);
    bolts.strike(route, { color: COLORS.volt, width: 3, life: 0.4, jitter: 9, branches: false });

    const marked = d.marked.filter(targetable);
    if (marked.length > 0) {
      ctx.time.hitstop(VOLT_TUNING.dashHitstopMs);
      this.discharge({ x: d.x0, y: d.y0 }, marked, extras, range, VOLT_TUNING.chainDamage, false);
      if (storm) {
        const t = timers.after(VOLT_TUNING.stormRunnerDelay, () => {
          this.pending.delete(t);
          const alive = marked.filter(targetable);
          if (alive.length > 0) this.discharge({ x: d.x0, y: d.y0 }, alive, extras, range, VOLT_TUNING.chainDamage, true);
        });
        this.pending.add(t);
      }
    } else {
      this.burst(end.x, end.y, VOLT_TUNING.burstDamage);
    }
    this.popMarkers(d);

    if (evolved) {
      const last = route[route.length - 1];
      safe.run('zones.trail', () =>
        ctx.zones.trail('electric', d.x0, d.y0, last.x, last.y, {
          duration: VOLT_TUNING.evolvedTrail.duration,
          dps: VOLT_TUNING.evolvedTrail.dps,
          status: { id: 'shock', stacks: 1, duration: 1.5 },
        }),
      );
    }

    if (this.h.flag('ARC_ECHO')) {
      const t = timers.after(VOLT_TUNING.arcEchoDelay, () => {
        this.pending.delete(t);
        this.echoDischarge(route);
      });
      this.pending.add(t);
    }
  }

  /** Chain lightning through `marked` in order (from `origin`), then `extras` jumps onward. */
  private discharge(origin: Vec, marked: EnemyApi[], extras: number, range: number, damage: number, secondary: boolean): void {
    const { ctx, safe, bolts } = this.h;
    const hit = new Set<EnemyApi>();
    const pts: Vec[] = [origin];
    let prev: Vec = origin;
    for (const e of marked) {
      if (!targetable(e)) continue;
      const opts = chainOpts(damage, 0, 0, secondary, 'arc_dash');
      safe.run('damage.chainLightning', () => ctx.damage.chainLightning(prev, e, opts));
      safe.run('fx.impact', () => ctx.fx.impact(e.x, e.y, 0, 'energy', COLORS.voltGlow));
      hit.add(e);
      prev = { x: e.x, y: e.y };
      pts.push(prev);
    }
    if (pts.length > 1) bolts.strike(pts, { color: COLORS.voltGlow, width: secondary ? 4 : 6, life: 0.34, jitter: 22 });
    // The mech releases the charge: a bolt from VOLT into the last mark.
    const p = ctx.player;
    bolts.strike([{ x: p.x, y: p.y }, prev], { color: COLORS.volt, width: 3, life: 0.22, jitter: 14 });

    if (extras > 0) {
      const opts = { ...chainOpts(damage, extras - 1, range, secondary, 'arc_dash'), exclude: hit };
      const more = safe.call('damage.chainLightning', () => ctx.damage.chainLightning(prev, null, opts), [] as EnemyApi[]);
      if (more.length > 0) bolts.strike([prev, ...more.map((e) => ({ x: e.x, y: e.y }))], { color: COLORS.volt, width: 3.5, life: 0.3, jitter: 18 });
    }
    sfx(ctx, safe, 'chain_zap', { volume: secondary ? 0.6 : 0.95, pitch: secondary ? 1.15 : 0.9 });
    safe.run('fx.shake', () => ctx.fx.shake(secondary ? 0.004 : 0.008, 140));
  }

  /** Nothing was marked: discharge around the end point. */
  private burst(x: number, y: number, damage: number): void {
    const { ctx, safe, bolts } = this.h;
    const r = VOLT_TUNING.burstRadius;
    const info: DamageInfo = { amount: damage, tags: ['electric'], source: 'player', sourceX: x, sourceY: y, statuses: [SHOCK], label: 'arc_dash' };
    safe.run('damage.explode', () => ctx.damage.explode(x, y, r, info, { fx: false, color: COLORS.volt }));
    safe.run('fx.ring', () => ctx.fx.ring(x, y, r, { color: COLORS.volt, width: 4, fromRadius: 24 }));
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + Math.random() * 0.6;
      const len = r * (0.65 + Math.random() * 0.35);
      bolts.strike([{ x, y }, { x: x + Math.cos(a) * len, y: y + Math.sin(a) * len }], { color: COLORS.voltGlow, width: 2.5, life: 0.22, jitter: 12 });
    }
    sfx(ctx, safe, 'chain_zap', { volume: 0.8, pitch: 0.75 });
    safe.run('fx.shake', () => ctx.fx.shake(0.005, 120));
  }

  /** ARC_ECHO: the path discharges again (60%), striking whatever stands on it now. */
  private echoDischarge(route: Vec[]): void {
    const { ctx, safe, bolts } = this.h;
    if (!safe.call('player.alive', () => ctx.player.alive, false)) return;
    bolts.strike(route, { color: COLORS.voltGlow, width: 4, life: 0.32, jitter: 16 });
    const candidates = targetableList(ctx, safe).map((e) => ({ item: e, areas: areasOf(e) }));
    const victims = sweepPath(route, MARK_HW + 6, MARK_HH, candidates);
    const damage = VOLT_TUNING.chainDamage * VOLT_TUNING.arcEchoMult;
    let prev: Vec = route[0];
    for (const e of victims) {
      const opts = chainOpts(damage, 0, 0, true, 'arc_echo');
      safe.run('damage.chainLightning', () => ctx.damage.chainLightning(prev, e, opts));
      prev = { x: e.x, y: e.y };
    }
    for (let i = 0; i < route.length; i += 2) {
      const pt = route[i];
      safe.run('fx.sparks', () => ctx.fx.sparks(pt.x, pt.y, -Math.PI / 2, { count: 3, color: COLORS.volt, speed: 200, spread: Math.PI }));
    }
    sfx(ctx, safe, 'chain_zap', { volume: 0.6, pitch: 0.8 });
  }

  private popMarkers(d: DashState): void {
    const scene = this.h.ctx.scene;
    for (const img of d.markers.values()) {
      if (!img.scene) continue;
      scene.tweens.add({
        targets: img,
        scale: 2.4,
        alpha: 0,
        duration: 180,
        onComplete: () => {
          this.h.untrack(img);
          img.destroy();
        },
      });
    }
    d.markers.clear();
  }

  // ───────────────────────────── Overload ─────────────────────────────

  ultimate(): void {
    const { ctx, safe, timers } = this.h;
    const p = ctx.player;
    const T = VOLT_TUNING;
    const scene = ctx.scene;

    safe.run('player.addBuff', () => p.addBuff('overload', [{ stat: 'moveSpeed', op: 'mul', value: T.overloadSpeed }], T.overloadDuration));
    safe.run('view.setReactorColor', () => p.view.setReactorColor(COLORS.voltGlow));
    safe.run('fx.flash', () => ctx.fx.flash(COLORS.voltGlow, 0.35, 180));
    safe.run('fx.shake', () => ctx.fx.shake(0.012, 320));
    safe.run('fx.ring', () => ctx.fx.ring(p.x, p.y, 320, { color: COLORS.volt, width: 6, fromRadius: 40 }));
    sfx(ctx, safe, 'overload');

    const aura = this.h.track(scene.add.image(p.x, p.y, texOr(scene, TEX.soft, ATEX.soft)));
    aura.setTint(COLORS.volt).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PLAYER - 1).setScale(0.5).setAlpha(0);
    scene.tweens.add({ targets: aura, scale: 3.4, alpha: 0.55, duration: 220, ease: 'Quad.Out' });
    const ring = this.h.track(scene.add.image(p.x, p.y, texOr(scene, TEX.ring, ATEX.ring)));
    ring.setTint(COLORS.voltGlow).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.FX_ADD).setScale(1.1).setAlpha(0.5);

    const count = Math.round(T.overloadDuration / T.overloadInterval);
    this.overload = {
      until: timers.now + T.overloadDuration,
      pulses: timers.every(T.overloadInterval, count, (i) => this.overloadPulse(i)),
      crackle: 0,
      aura,
      ring,
    };
    // Radial opening burst.
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      this.h.bolts.strike([{ x: p.x, y: p.y }, { x: p.x + Math.cos(a) * 260, y: p.y + Math.sin(a) * 260 }], {
        color: COLORS.voltGlow,
        width: 3,
        life: 0.3,
        jitter: 26,
      });
    }
  }

  /** One wave: lightning from VOLT through every enemy inside the camera view. */
  private overloadPulse(i: number): void {
    const { ctx, safe, bolts } = this.h;
    const p = ctx.player;
    const view = ctx.scene.cameras.main.worldView;
    const targets = targetableList(ctx, safe).filter((e) => view.contains(e.x, e.y));
    const from = { x: p.x, y: p.y };
    if (targets.length === 0) {
      // Nothing to hit: the reactor still crackles outward so the ultimate never feels idle.
      for (let k = 0; k < 4; k++) {
        const a = Math.random() * Math.PI * 2;
        bolts.strike([from, { x: from.x + Math.cos(a) * 170, y: from.y + Math.sin(a) * 170 }], { color: COLORS.volt, width: 2.5, life: 0.2, jitter: 20 });
      }
      sfx(ctx, safe, 'chain_zap', { volume: 0.35, pitch: 1.3 });
      return;
    }
    const order = nearestNeighbourOrder(from, targets);
    const pts: Vec[] = [from];
    let prev: Vec = from;
    for (const idx of order) {
      const e = targets[idx];
      const opts = chainOpts(VOLT_TUNING.overloadDamage, 0, 0, false, 'overload');
      safe.run('damage.chainLightning', () => ctx.damage.chainLightning(prev, e, opts));
      prev = { x: e.x, y: e.y };
      pts.push(prev);
    }
    bolts.strike(pts, { color: i % 2 === 0 ? COLORS.voltGlow : COLORS.volt, width: 5, life: 0.32, jitter: 24 });
    safe.run('fx.shake', () => ctx.fx.shake(0.004, 110));
    sfx(ctx, safe, 'chain_zap', { volume: 0.8, pitch: 0.85 + (i % 3) * 0.1 });
  }

  private endOverload(): void {
    const o = this.overload;
    if (!o) return;
    this.overload = null;
    o.pulses.cancel();
    const { ctx, safe } = this.h;
    const p = ctx.player;
    safe.run('view.setReactorColor', () => p.view.setReactorColor(p.def.reactorColor));
    for (const img of [o.aura, o.ring]) {
      if (!img.scene) continue;
      ctx.scene.tweens.add({
        targets: img,
        alpha: 0,
        scale: img.scale * 1.4,
        duration: 260,
        onComplete: () => {
          this.h.untrack(img);
          img.destroy();
        },
      });
    }
    safe.run('fx.ring', () => ctx.fx.ring(p.x, p.y, 150, { color: COLORS.volt, width: 3, fromRadius: 60 }));
  }

  // ───────────────────────────── frame ─────────────────────────────

  update(dt: number): void {
    if (this.dash) this.stepDash();
    if (this.dash) {
      for (const [e, img] of this.dash.markers) {
        if (!img.scene) continue;
        img.setPosition(e.x, e.y - e.radius - 18);
        img.rotation += dt * 4;
      }
    }

    const o = this.overload;
    if (o) {
      const { ctx, timers, bolts } = this.h;
      const p = ctx.player;
      if (timers.now >= o.until) {
        this.endOverload();
        return;
      }
      const pulse = 0.5 + 0.5 * Math.sin(timers.now * 18);
      if (o.aura.scene) o.aura.setPosition(p.x, p.y).setAlpha(0.35 + pulse * 0.25);
      if (o.ring.scene) {
        o.ring.setPosition(p.x, p.y);
        o.ring.rotation += dt * 3;
        o.ring.setScale(1 + pulse * 0.12);
      }
      // Body crackle (cosmetic).
      o.crackle -= dt;
      if (o.crackle <= 0) {
        o.crackle = 0.06 + Math.random() * 0.06;
        const a = Math.random() * Math.PI * 2;
        const r0 = 18 + Math.random() * 16;
        const r1 = r0 + 30 + Math.random() * 40;
        bolts.strike(
          [
            { x: p.x + Math.cos(a) * r0, y: p.y + Math.sin(a) * r0 * 1.6 },
            { x: p.x + Math.cos(a) * r1, y: p.y + Math.sin(a) * r1 * 1.3 },
          ],
          { color: COLORS.voltGlow, width: 1.8, life: 0.1, jitter: 7, branches: false },
        );
      }
    }
  }

  interrupt(): void {
    const d = this.dash;
    if (d && !d.done) {
      d.done = true;
      d.safety.cancel();
      this.popMarkers(d);
      this.dash = null;
    }
    for (const t of this.pending) t.cancel();
    this.pending.clear();
    this.endOverload();
  }

  destroy(): void {
    this.interrupt();
  }
}

/** Hit test rects of an enemy (falls back to a box around its radius). */
function areasOf(e: EnemyApi): readonly Rect[] {
  try {
    const a = e.hitAreas();
    if (a.length > 0) return a;
  } catch {
    // fall through
  }
  return [{ x: e.x - e.radius, y: e.y - e.radius, w: e.radius * 2, h: e.radius * 2 }];
}

/** chainLightning options (plus the label/statuses extras combat understands). */
function chainOpts(damage: number, count: number, range: number, secondary: boolean, label: string) {
  return {
    count,
    range,
    damage,
    tags: CHAIN_TAGS.slice(),
    color: COLORS.volt,
    secondary,
    label,
  };
}
